import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  DatePicker,
  Select,
  Input,
  Button,
  Space,
  Tag,
  Popconfirm,
} from 'antd';
import { SearchOutlined, ReloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { getLogList, getOperationTypes, clearLogs } from '../../services/log';
import { OperationLog, PaginatedResponse } from '../../types';
import { antdMessage } from '../../utils/antdGlobal';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const LogManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [clearLoading, setClearLoading] = useState(false);
  const [data, setData] = useState<PaginatedResponse<OperationLog> | null>(null);
  const [operationTypes, setOperationTypes] = useState<string[]>([]);
  const [filters, setFilters] = useState({
    operator: '',
    operationType: undefined,
    operationResult: undefined,
    dateRange: null as any,
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });

  const fetchData = async (params?: any) => {
    setLoading(true);
    try {
      const queryParams: any = {
        page: pagination.current,
        pageSize: pagination.pageSize,
        ...params,
      };

      if (filters.operator) {
        queryParams.operator = filters.operator;
      }
      if (filters.operationType) {
        queryParams.operationType = filters.operationType;
      }
      if (filters.operationResult) {
        queryParams.operationResult = filters.operationResult;
      }
      if (filters.dateRange && filters.dateRange.length === 2) {
        queryParams.startDate = filters.dateRange[0].format('YYYY-MM-DD');
        queryParams.endDate = filters.dateRange[1].format('YYYY-MM-DD');
      }

      const res = await getLogList(queryParams);
      setData(res);
    } catch (error) {
      console.error('获取日志失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOperationTypes = async () => {
    try {
      const types = await getOperationTypes();
      setOperationTypes(types);
    } catch (error) {
      console.error('获取操作类型失败:', error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchOperationTypes();
  }, [pagination.current, pagination.pageSize]);

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchData({ page: 1 });
  };

  const handleReset = () => {
    setFilters({
      operator: '',
      operationType: undefined,
      operationResult: undefined,
      dateRange: null,
    });
    setPagination({ current: 1, pageSize: 20 });
    fetchData({ page: 1 });
  };

  const handleClearLogs = async () => {
    setClearLoading(true);
    try {
      const res = await clearLogs();
      antdMessage.success(`已成功清空 ${res.deletedCount} 条操作日志`);
      setPagination({ current: 1, pageSize: 20 });
      fetchData({ page: 1 });
      fetchOperationTypes();
    } catch {
      // 错误已由请求拦截器统一提示
    } finally {
      setClearLoading(false);
    }
  };

  const columns = [
    {
      title: '操作时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作人',
      dataIndex: 'operatorAccount',
      key: 'operatorAccount',
      width: 120,
      render: (account: string, record: OperationLog) =>
        account || record.operatorType,
    },
    {
      title: '操作类型',
      dataIndex: 'operationType',
      key: 'operationType',
      width: 150,
    },
    {
      title: '操作内容',
      dataIndex: 'operationContent',
      key: 'operationContent',
      ellipsis: true,
    },
    {
      title: 'IP地址',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 140,
    },
    {
      title: '结果',
      dataIndex: 'operationResult',
      key: 'operationResult',
      width: 100,
      render: (result: string) => (
        <Tag color={result === 'success' ? 'success' : 'error'}>
          {result === 'success' ? '成功' : '失败'}
        </Tag>
      ),
    },
  ];

  return (
    <div>
      <Card title="操作日志">
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="操作人账号"
            value={filters.operator}
            onChange={(e) => setFilters({ ...filters, operator: e.target.value })}
            style={{ width: 150 }}
          />
          <Select
            placeholder="操作类型"
            value={filters.operationType}
            onChange={(value) => setFilters({ ...filters, operationType: value })}
            style={{ width: 150 }}
            allowClear
          >
            {operationTypes.map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="操作结果"
            value={filters.operationResult}
            onChange={(value) => setFilters({ ...filters, operationResult: value })}
            style={{ width: 120 }}
            allowClear
          >
            <Option value="success">成功</Option>
            <Option value="failure">失败</Option>
          </Select>
          <RangePicker
            value={filters.dateRange}
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            查询
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            重置
          </Button>
          <Popconfirm
            title="确认清空操作日志"
            description="此操作将删除所有操作日志记录，且不可恢复，是否继续？"
            onConfirm={handleClearLogs}
            okText="确认清空"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={clearLoading}
            >
              清空日志
            </Button>
          </Popconfirm>
        </Space>

        <Table
          columns={columns}
          dataSource={data?.list}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPagination({ current: page, pageSize: pageSize || 20 });
            },
          }}
        />
      </Card>
    </div>
  );
};

export default LogManagement;
